// AP/AR Approval Decision (docs/workflow.md step 6, issue 3 2026-08-11).
// Modeled on ImportControlScreen.tsx: a single-status, click-into-each queue
// with no bulk actions, because AP/AR now carries the review work Import
// Control used to do for every invoice — Import Control narrowed to settling
// an ambiguous company alone (owner ruling 4), so most invoices skip it
// entirely and arrive here still carrying whatever flags Matching raised.
//
// AP/AR is MANDATORY, not present-if-staffed like the three tiers below it: an
// invoice never routes around it, so a company with nobody appointed `ap-ar`
// freezes here — the Pipeline dialog's warning icon on this node exists for
// exactly that.
//
// Company-scoped, exactly like Department/Director/Oversight: the queue is
// whatever the caller's `ap-ar` seat covers, and `canDecide` is the same
// per-row hint those screens use. Two outcomes, no deny (same as Import
// Control): `approved` releases the invoice — the company's approvers gain
// access — and `archived not an invoice` matches a document that turns out not
// to be a payable at all.
//
// AP/AR mounts the SAME shared editor Import Control has always used
// (`EditableInvoiceForm`, `editStep={DecisionStep.ApAr}`) — the full surface,
// unlocked: flags resolvable via Mark resolved, amounts editable, every line's
// expense type editable. The column rule is enforced INSIDE that editor, not
// by a narrowing prop here: it never renders an Account picker and never a
// "Who is it for?" column — the cost center is Department Approval's business
// or later, and the composed account belongs to Export Control and Manual
// Entry. A caller with no `ap-ar` seat covering the invoice's own company
// falls back to the read-only view (no `editStep`), with the disabled
// Approve/Not An Invoice pair and `NOT_AUTHORIZED_REASON` below.
//
// No per-row actions (owner item 5, 2026-08-13): the queue used to carry
// icon-only Approve/Archive buttons beside Open, but a reviewer must open the
// invoice — see its flags, amounts and the editable form — before deciding
// anything, so the row buttons offered a click that either 409'd or hid what
// it was approving. The only way to act here now is Open, then the modal's
// own editable action bar (decidable) or its disabled pair (not decidable).
import { useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toast,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { ScreenTitle } from '../components/ScreenTitle'
import type { Invoice } from '../lib/types'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision, loadStuckNodes } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, canOpenAssignments, canPreviewPdf, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  // One flex child of the modal's own actionBar row (`InvoiceDetailModal.tsx`'s
  // `actions` slot) — stacked so the stuck-company banner sits above the
  // button pair rather than fighting them for the row's horizontal space.
  modalActions: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end', minWidth: 0 },
  modalActionButtons: { display: 'flex', gap: '8px' },
  stuckBanner: { maxWidth: '480px' },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

const NOT_AUTHORIZED_REASON =
  "Only an AP/AR holder assigned to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."

export function ApArApprovalScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingApAr], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // One source for one truth: the modal's editable-vs-read-only branch reads
  // this rather than computing `canDecide` on the open invoice a second time.
  const openDecidable = !!openInvoice && canDecide(session, DecisionStep.ApAr, openInvoice)
  const [archiving, setArchiving] = useState<Invoice | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Owner item 6: AP/AR is mandatory and never skipped, so a company with
  // nobody appointed `ap-ar` freezes every invoice at this step for EVERYONE,
  // including Admin — `canDecide` alone gives no way to tell "not my company"
  // apart from "nobody's company", because it only knows the caller's own
  // seats. `GET /queues/stuck` (`loadStuckNodes`) is the one place that
  // distinction is visible: it names every company with nothing staffed for a
  // tier. Refetched whenever the queue itself does — the top-bar Refresh
  // bumps `refreshNonce`, and `settle()` (below) bumps `stuckRefreshNonce` on
  // every local action (approve, archive) so newly appointing someone clears
  // the warning at the same cadence the row disappears, without waiting for
  // the next top-bar refresh.
  const [stuckCompanies, setStuckCompanies] = useState<Map<string, string>>(new Map())
  const [stuckRefreshNonce, setStuckRefreshNonce] = useState(0)
  useEffect(() => {
    let cancelled = false
    loadStuckNodes()
      .then((stuck) => {
        if (cancelled) return
        const byId = new Map<string, string>()
        for (const c of stuck['ap-ar']?.companies ?? []) byId.set(c.company_id, c.company_name)
        setStuckCompanies(byId)
      })
      .catch(() => {
        // Best-effort: a failed fetch just means no warning renders, not a
        // screen error — the underlying freeze (and the Pipeline dialog's own
        // warning icon) is unaffected.
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce, stuckRefreshNonce])

  const canAppoint = canOpenAssignments(session)

  /** The "no ap-ar holder" banner for one invoice's company, or undefined. */
  function unstaffedNotice(invoice: Invoice) {
    const companyName = stuckCompanies.get(invoice.company_id ?? '')
    if (!companyName) return undefined
    return (
      <MessageBar intent="warning" className={s.stuckBanner}>
        <MessageBarBody>
          <MessageBarTitle>No AP/AR holder for {companyName}</MessageBarTitle>
          Nobody can act on this invoice until someone is assigned AP/AR for {companyName}
          {canAppoint ? ' — assign one on the Assignments tab.' : '; ask an admin or director to assign one.'}
        </MessageBarBody>
      </MessageBar>
    )
  }
  const { dispatchToast } = useToastController(TOASTER_ID)

  function settle() {
    setOpenInvoice(null)
    setArchiving(null)
    void reload()
    onQueuesChanged?.()
    // Bumps the stuck-companies effect above too, so a concurrent appointment
    // made elsewhere (on the Assignments tab) is reflected here at the same
    // cadence as the queue's own reload, rather than waiting for the next
    // top-bar Refresh to bump `refreshNonce`.
    setStuckRefreshNonce((n) => n + 1)
  }

  async function approve(invoice: Invoice) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.Approved,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function archiveNotAnInvoice(invoice: Invoice, reason: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Archived — Not an Invoice</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>AP/AR Approval</ScreenTitle>
        <Body1 className={s.sub}>For each flagged invoice, resolve all outstanding issues to release for approval.</Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue
          invoices={filtered}
          showFlags
          onOpen={setOpenInvoice}
          rowNotice={unstaffedNotice}
        />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={canPreviewPdf(session)}
        user={user}
        editStep={openDecidable ? DecisionStep.ApAr : undefined}
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        onReleased={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        onArchived={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Archived — Not an Invoice</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        actions={
          // Rendered by the modal on its READ-ONLY branch only — a decidable
          // reviewer gets the editable form's own Save & Release / Not An
          // Invoice bar instead (`editStep` above), the same swap Import
          // Control has always had. A non-decidable viewer keeps this
          // disabled pair — with the unstaffed-company banner above it when
          // that is WHY it's disabled (owner item 6), rather than leaving the
          // hover-only NOT_AUTHORIZED_REASON title as the only explanation.
          openInvoice &&
          (() => {
            const notice = unstaffedNotice(openInvoice)
            return (
              <div className={s.modalActions}>
                {notice}
                <div className={s.modalActionButtons}>
                  <Button
                    className={btn.approve}
                    icon={<CheckmarkRegular />}
                    disabled={submitting || !openDecidable}
                    title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                    onClick={() => approve(openInvoice)}
                  >
                    Approve
                  </Button>
                  <Button
                    className={btn.deny}
                    icon={<DismissRegular />}
                    disabled={submitting || !openDecidable}
                    title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                    onClick={() => setArchiving(openInvoice)}
                  >
                    Not An Invoice
                  </Button>
                </div>
              </div>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!archiving}
        // The heading is the button text that opens this dialog (owner item
        // 5, second sentence, 2026-08-12) — "Not An Invoice" in the modal.
        title="Not An Invoice"
        body="This document will be archived as not an invoice. This comment is recorded on the record and is visible in the decision history."
        requireReason
        reasonLabel="Reason"
        confirmLabel="Archive"
        destructive
        submitting={submitting}
        onCancel={() => setArchiving(null)}
        onConfirm={(reason) => archiving && archiveNotAnInvoice(archiving, reason)}
      />
    </div>
  )
}
